import { useCallback } from 'react';
import { gate } from 'hub-http/gates';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useSegment, useMutateSegment } from 'crm-index-visualization-core';
import { useCurrentPipelineIdActions } from './useCurrentPipelineIdActions';
import { trackPipelineSwitcherChanged } from '../../../tracking/indexPageTracking';
import { ALL_PIPELINES_VALUE } from '../constants/AllPipelinesValue';
import { useHasAllGates } from '../../../auth/hooks/useHasAllGates';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
export function useSwitchPipelines({
  objectTypeId,
  pageType
}) {
  const hasAllGate = useHasAllGates();
  const useVizCoreLib = hasAllGate(gate('CRM:Index:UseVizCoreLib'));
  const {
    changePipeline
  } = useCurrentPipelineIdActions();
  const [saveSegmentDraft] = useMutateSegment({
    onError(e) {
      Raven.captureException(e);
    }
  });
  const {
    data: segment
  } = useSegment();

  // Legacy implementation of handleChange
  const handleChangeLegacy = useCallback(selectedPipeline => {
    var _selectedPipeline$pip;
    const selectedPipelineId = (_selectedPipeline$pip = selectedPipeline === null || selectedPipeline === void 0 ? void 0 : selectedPipeline.pipelineId) !== null && _selectedPipeline$pip !== void 0 ? _selectedPipeline$pip : ALL_PIPELINES_VALUE;
    trackPipelineSwitcherChanged({
      objectTypeId,
      pipelineId: selectedPipelineId,
      type: 'manuallyChanged',
      pageType
    });
    const selectedPipelineLabel = selectedPipelineId === ALL_PIPELINES_VALUE ? unescapedText('contentTopbar.allPipelinesVerbose') : selectedPipeline === null || selectedPipeline === void 0 ? void 0 : selectedPipeline.label;
    if (!selectedPipelineLabel) {
      throw new Error('Unable to find selected pipeline');
    }
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "indexPage.pipelineSelector.pipelineChanged",
        options: {
          pipelineName: selectedPipelineLabel
        }
      }),
      type: 'success'
    });
    return changePipeline(selectedPipelineId);
  }, [changePipeline, objectTypeId, pageType]);

  // New implementation of handleChange using vizcore
  const handleChangeNew = useCallback(selectedPipeline => {
    var _selectedPipeline$pip2;
    if (!segment) {
      return;
    }
    const selectedPipelineId = (_selectedPipeline$pip2 = selectedPipeline === null || selectedPipeline === void 0 ? void 0 : selectedPipeline.pipelineId) !== null && _selectedPipeline$pip2 !== void 0 ? _selectedPipeline$pip2 : ALL_PIPELINES_VALUE;
    const selectedPipelineLabel = selectedPipelineId === ALL_PIPELINES_VALUE ? unescapedText('contentTopbar.allPipelinesVerbose') : selectedPipeline === null || selectedPipeline === void 0 ? void 0 : selectedPipeline.label;
    if (!selectedPipelineLabel) {
      throw new Error('Unable to find selected pipeline');
    }
    const segmentWithUpdatedPipeline = segment.with({
      pipelineId: selectedPipelineId === ALL_PIPELINES_VALUE ? null : selectedPipelineId
    });
    void saveSegmentDraft(segmentWithUpdatedPipeline).then(() => {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "indexPage.pipelineSelector.pipelineChanged",
          options: {
            pipelineName: selectedPipelineLabel
          }
        }),
        type: 'success'
      });
      trackPipelineSwitcherChanged({
        objectTypeId,
        pipelineId: selectedPipelineId,
        type: 'manuallyChanged',
        pageType
      });
    });
  }, [objectTypeId, pageType, segment, saveSegmentDraft]);

  // Choose the appropriate handler based on the gate
  return useVizCoreLib ? handleChangeNew : handleChangeLegacy;
}